import { SympyBuildError } from "./shared";

import type { SemanticExpr } from "../types";

/**
 * Renders a SemanticExpr operator tree into a Python fragment. Arithmetic operators are emitted as
 * structured SymPy constructors (`sp.Add`, `sp.Mul`, `sp.Pow`, …) via the `default` branch, so the
 * output is unambiguous without parentheses and round-trips with the SymPy serializer. A few
 * input-only forms (`Neg`, `Frac`) are emitted explicitly; the residual precedence helpers exist
 * only for `Neg`.
 */

// Precedence levels (higher binds tighter).
const PREC_ADD = 1;
const PREC_MUL = 2;
const PREC_POW = 3;
const PREC_ATOM = 4;

type Lookup = (name: string) => string | undefined;

function precedenceOf(expr: SemanticExpr): number {
  if (expr.type === "operator") {
    switch (expr.name) {
      case "Add":
      case "Neg":
        return PREC_ADD;
      case "Mul":
      case "Frac":
        return PREC_MUL;
      case "Pow":
        return PREC_POW;
      default:
        return PREC_ATOM;
    }
  }
  return PREC_ATOM;
}

/** Emit `expr` and wrap in parentheses if its precedence is below `minPrec`. */
function emitWrapped(expr: SemanticExpr, lookup: Lookup, minPrec: number): string {
  const text = emit(expr, lookup);
  return precedenceOf(expr) < minPrec ? `(${text})` : text;
}

function emit(expr: SemanticExpr | undefined, lookup: Lookup): string {
  if (!expr) return "";

  switch (expr.type) {
    case "symbol": {
      const mapped = lookup(expr.name);
      return mapped ?? expr.name;
    }
    case "number":
      return expr.kind === "integer" ? `sp.Integer(${expr.value})` : `sp.Rational("${expr.value}")`;
    case "imaginary_unit":
      return "sp.I";
    case "infinity":
      return expr.kind === "negative" ? "-sp.oo" : "sp.oo";
    case "unit":
      return `units.${expr.name}`;
    case "rational":
      return `sp.Rational(${expr.p}, ${expr.q})`;
    case "matrix":
      return renderMatrix(expr, (e) => emit(e, lookup));
    case "plus_minus":
      throw new SympyBuildError("PlusMinus operator is not supported in SymPy input");
    case "function":
      return emitFunction(expr, lookup);
    case "operator":
      return emitOperator(expr, lookup);
  }
}

function renderMatrix(
  expr: Extract<SemanticExpr, { type: "matrix" }>,
  render: (e: SemanticExpr) => string,
): string {
  const { rows, cols, data } = expr;
  const cellStrs = data.map(render);
  const rowArrays: string[] = [];
  for (let i = 0; i < rows; i++) {
    rowArrays.push(`[${cellStrs.slice(i * cols, (i + 1) * cols).join(",")}]`);
  }
  return `sp.ImmutableMatrix([${rowArrays.join(",")}])`;
}

function emitFunction(expr: Extract<SemanticExpr, { type: "function" }>, lookup: Lookup): string {
  // `name` is already a fully-qualified Python path (e.g. "sp.sin",
  // "sympy_json.statistics.Median"), so emit it verbatim — no alias, no prefix.
  return `${expr.name}(${expr.args
    .map((a) => {
      return Array.isArray(a) ? `(${a.map((x) => emit(x, lookup)).join(",")})` : emit(a, lookup);
    })
    .join(",")})`;
}

function emitOperator(expr: Extract<SemanticExpr, { type: "operator" }>, lookup: Lookup): string {
  switch (expr.name) {
    case "Pi":
      return "sp.pi";
    case "Exp1":
      return "sp.E";
    case "GoldenRatio":
      return "sp.GoldenRatio";
    case "Catalan":
      return "sp.Catalan";
    case "EmptySet":
      return "sp.S.EmptySet";
    case "Reals":
      return "sp.S.Reals";
    case "Integers":
      return "sp.S.Integers";
    case "Naturals":
      return "sp.S.Naturals";
    case "Naturals0":
      return "sp.S.Naturals0";
    case "Rationals":
      return "sp.S.Rationals";
    case "Complexes":
      return "sp.S.Complexes";

    case "Indexed":
      return `${emit(expr.args[0], lookup)}[${emit(expr.args[1], lookup)}]`;

    case "BooleanTrue":
      return "True";
    case "BooleanFalse":
      return "False";

    /* ---- input-only operations ---- */
    case "Neg":
      return `-${emitWrapped(expr.args[0], lookup, PREC_MUL)}`;

    case "Frac": {
      return `sp.Mul(${emit(expr.args[0], lookup)}, sp.Pow(${emit(expr.args[1], lookup)}, sp.Integer(-1)))`;
    }

    case "MatrixScalar":
      return expr.args.map((a) => emit(a, lookup)).join(",");

    case "Call": {
      const head = emit(expr.args[0], lookup);
      const callArgs = expr.args
        .slice(1)
        .map((a) => emit(a, lookup))
        .join(",");
      return `${head}(${callArgs})`;
    }

    case "Root": {
      const radicand = emit(expr.args[0], lookup);
      const index = emit(expr.args[1], lookup);
      return `sp.root(${radicand}, ${index || 2})`;
    }

    case "Tuple":
      return `(${expr.args.map((a) => emit(a, lookup)).join(",")})`;

    default:
      // Generic operator fallback: operator names are bare, so qualify with the sympy module.
      // handles Integral / Sum / Product / Limit / Derivative
      return `sp.${expr.name}(${expr.args
        .map((a) => {
          return Array.isArray(a)
            ? `(${a.map((x) => emit(x, lookup)).join(",")})`
            : emit(a, lookup);
        })
        .join(",")})`;
  }
}

/**
 * Public entry: render a full SemanticExpr to Python, substituting symbol display names through
 * the `variables` map (display name → var0/const1/…).
 */
export function semanticToSympyCode(expr: SemanticExpr, variables: Map<string, string>): string {
  return emit(expr, (name) => variables.get(name));
}
